import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { AlertsType } from '@/constants/alerts.constant';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import useComponentVisible from '../../../../../hooks/useComponentVisible';
import CopyVariable from '../../../../common/CopyVariable';
import AddNoteButton from '../../AddNoteButton';
import DeleteButton from '../../DeleteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import { MacBotButtonSettingForm } from './scenario-button-setting-form.component';
import ScenarioSide from './scenario-side';
import { TextInputWithHint } from './TextInputWithHint';

export const MacBotSpeechRecordingForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  config,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow?: (item: boolean) => void;
  itemData: any;
  parentId: string | null;
  tree: any[];
  config: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
}) => {
  const { t } = useTranslation();
  const [showButtonSetting, setShowButtonSetting] = useState(false);
  const [selectedButtonToEdit, setSelectedButtonToEdit] = useState<number>(-1);
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const userId = useAppSelector(selectUseId);
  const { ref, isComponentVisible } = useComponentVisible(showContextMenu);
  const [cloneJson, setCloneJson] = useState<IResponseForm | null>(null);

  interface IResponseForm extends INoteForm {
    BlockTitle?: string;
  }

  const schema = Yup.object({
    BlockTitle: Yup.string().required(t(AlertsType.ISKBKO025)).max(30, t('Wizard/validationLabels/key16')),
    ...getNoteSchema(t),
  });

  const {
    register,
    formState: { errors },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control,
  } = useForm<IResponseForm>({
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
    defaultValues: {
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });
  //Check Show Button Delete
  useEffect(() => {
    if (itemData) {
      const find = tree?.find(dr => dr.id == itemData.id);
      if (find) {
        if (find.parent) {
          setShowDeleteButton(true);
        } else {
          setShowDeleteButton(false);
        }
      }
    }
  }, [tree, itemData]);

  const [contextMenuList, setContextMenuList] = useState<any[]>([]);

  useEffect(() => {
    reset({
      BlockTitle: itemData?.title ?? '',
      notes: itemData?.notes,
    });

    setCloneJson({
      BlockTitle: itemData?.title ?? '',
      showNoteForm: itemData?.notes?.length > 0 || false,
      notes: itemData?.notes,
    });

    if (!itemData) {
      setShowDeleteButton(false);
    }
  }, [itemData]);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
  };

  const removeButtonHandler = (btnIndex: number) => {
    setQuickReplies(prev => prev.filter((p, index) => index !== btnIndex));
  };
  const addButtonHandler = (qBtn: number) => {
    setQuickReplies(prev => [...prev, qBtn]);
  };

  const editButtonHandler = (_index: number, qRep: any) => {
    setQuickReplies(prev => prev.map((p, index) => (index === _index ? qRep : p)));

    setSelectedButtonToEdit(-1);
  };

  const closeButtonSettingModal = () => {
    setShowButtonSetting(false);
    setSelectedButtonToEdit(-1);
  };

  const ButtonSubmit = (data: IResponseForm) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.SpeechRecording,
        title: data.BlockTitle,
        parent: itemData.parent,
        notes: data.notes,
      };

      if (
        JSON.stringify(cloneJson) ==
        JSON.stringify({
          BlockTitle: data.BlockTitle,
          notes: data.notes,
        })
      ) {
        setShow?.(false);
        return;
      }

      changeScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    } else {
      const _data = {
        type: BlockType.SpeechRecording,
        title: data.BlockTitle,
        parent: parentId,
        notes: data.notes,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    }
  };

  const getVariableName = () => {
    const data = watch('BlockTitle');
    if (data) {
      return `${data}_user_speech_text`;
    }

    return '';
  };

  useEffect(() => {
    setShowContextMenu(false);
  }, [isComponentVisible]);

  useEffect(() => {
    if (config?.metaData?.variables) {
      setContextMenuList([...config.metaData.variables]);
    }
  }, [config]);

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowSpeechRecording}
          onClose={() => {
            reset();
          }}
        />

        <div className="tw-w-full tw-h-full tw-px-3 tw-py-2 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex tw-font-[Lexend]">
          <TextInputWithHint
            label={t('MacBotResponseForm/key1')}
            name="BlockTitle"
            register={register('BlockTitle')}
            error={errors.BlockTitle}
            max={30}
            maxLength={30}
            HintMessage={['MacBotResponseForm/maxlenght']}
          />

          <Form.Group className="tw-w-full ">
            <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t('MacBotCreateVariableForm/key13')}
            </Form.Label>

            <CopyVariable text={getVariableName()} noTitle={true} />
          </Form.Group>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {showDeleteButton && <DeleteButton deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-speech-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>

      <MacBotButtonSettingForm
        show={showButtonSetting}
        closeModal={closeButtonSettingModal}
        addQuickReplies={addButtonHandler}
        editQuickReplies={editButtonHandler}
        selectedButtonToEdit={selectedButtonToEdit}
        quickReplies={quickReplies}
        removeButton={removeButtonHandler}
        tree={tree}
      />
    </React.Fragment>
  );
};
